import { Component, OnInit, inject } from '@angular/core';
import { Params, Router } from '@angular/router';
import { AuthenticationService } from '../../../backend/api';
import { AuthenticationContextDatasourceService } from '../../../backend/datasource/authentication-context-datasource.service';
import { AuthenticationMethod, AuthenticationMethodType } from '../../../backend/model';
import { ThemeHandlerService } from '../../service/theme-handler.service';
import { IDCardModule } from '@inera/ids-angular/components/card';
import { NgTemplateOutlet } from '@angular/common';
import { ThemeType } from '../../../backend/model/theme-type';

@Component({
  selector: 'idp-authentication-method',
  imports: [IDCardModule, NgTemplateOutlet],
  templateUrl: 'authentication-method.component.html',
  styleUrls: ['./authentication-method.component.scss']
})
export class AuthenticationMethodComponent implements OnInit {
  private router = inject(Router);
  private authenticationContextDatasource = inject(AuthenticationContextDatasourceService);
  private authenticationService = inject(AuthenticationService);
  private themeService = inject(ThemeHandlerService);

  public methods: AuthenticationMethod[];

  public loading = false;
  public clientInfo;
  public mainTheme = '';

  public themeType = ThemeType;

  ngOnInit(): void {
    this.themeService.getTheme().subscribe(theme => {
      this.mainTheme = theme.value.value.theme.mainTheme;
    });
    this.getMethods();
    this.clientInfo = this.authenticationContextDatasource.getClientInfo();
  }

  public selectMethod(method: AuthenticationMethod | any, forceSelect: boolean): void {
    if (this.loading && !forceSelect) {
      return;
    }

    this.loading = true;

    const methodType: string = method.type;
    const authMethodType: AuthenticationMethodType = AuthenticationMethodType[methodType];
    if (authMethodType !== AuthenticationMethodType.MTLS) {
      this.submitOobAuthentication(method);
    } else {
      this.submitMtls(method);
    }
  }

  private submitOobAuthentication(method: AuthenticationMethod): void {
    const params: Params = {
      authMethodType: method.type,
      id: this.authenticationContextDatasource.getSessionId()
    };

    const baseUrl = this.router.url.split('?')[0];
    this.router.navigate([baseUrl + method.redirectUrl], { queryParams: params });
  }

  private submitMtls(method: AuthenticationMethod): void {
    let redirect: string;
    const baseUrl = this.router.url.split('?')[0];
    if (method?.rotatingRedirectUrl?.length > 0) {
      redirect = method.rotatingRedirectUrl + baseUrl + '/x509';
    } else {
      redirect = method.redirectUrl + baseUrl + '/x509';
    }

    redirect += `?authMethodType=${method.type}&id=${this.authenticationContextDatasource.getSessionId()}`;

    window.location.href = redirect;
  }

  private getMethods(): void {
    this.loading = true;

    this.authenticationService
      .getMethods(this.authenticationContextDatasource.getIdp(), this.authenticationContextDatasource.getSessionId())
      .subscribe(
        result => {
          this.methods = result.sort((a, b) => a.priorityOrder - b.priorityOrder);

          const filteredMethod = this.methods.filter(
            m => m.type === this.authenticationContextDatasource.getAuthMethodType()
          );
          if (filteredMethod?.length > 0) {
            this.selectMethod(filteredMethod[0], true);
          } else if (this.methods?.length === 1) {
            this.selectMethod(this.methods[0], true);
          } else {
            this.loading = false;
          }
        },
        () => {
          this.loading = false;
        }
      );
  }

  public getMethodText(methodName: string): string {
    const sameDevice = 'denna';
    const otherDevice = 'annan';

    if (methodName.includes(sameDevice)) {
      return this.createModifiedMethodText(methodName, sameDevice);
    } else if (methodName.includes(otherDevice)) {
      return this.createModifiedMethodText(methodName, otherDevice);
    }

    return methodName;
  }

  private createModifiedMethodText(methodName: string, separator: string): string {
    const separatedMethodName = methodName.split(separator);

    return separatedMethodName[0] + '<b>' + separator + '</b>' + separatedMethodName[1];
  }

  public getTitle(): string {
    return this.authenticationContextDatasource.getIsSignature() ? 'Jag signerar hos' : 'Jag legitimerar mig hos';
  }

  public getTypeText(): string {
    return this.authenticationContextDatasource.getIsSignature() ? 'Välj signeringsmetod' : 'Välj legitimeringsmetod';
  }
}
